import { memo, MouseEvent, useCallback, useRef, useState } from "react";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/shadcn";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { TComment } from "../model/types/types";
import { getUserFullName, getUserInitials } from "../lib/lib";
import { ClickAwayListener, FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CommentActions } from "./_CommentActions";
import { ReplyEditInputField } from "./_ReplyEditInputField";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { CommentReplies } from "./_CommentReplies";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { scrollIntoView } from "@/zapi-frontend/shared/lib/scrollIntoView";

type TCommentProps = { comment: TComment; onDelete: (id: string) => void; markedText: string };

export const Comment = memo((props: TCommentProps) => {
  const { comment, onDelete, markedText } = props;

  const { firstName, lastName, id: userId } = useMeStore();
  const { runTimeout } = useScopedTimeout();

  const replyFieldRef = useRef<HTMLTextAreaElement>(null);
  const editFieldRef = useRef<HTMLTextAreaElement>(null);

  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [isShake, setIsShake] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const isCanEdit = comment.author.id === userId;

  const handleReplyToComment = useCallback(() => {
    setIsShake(false);
    setIsReplyOpen(true);

    if (replyFieldRef.current) replyFieldRef.current.focus();
  }, []);

  const handleClickAway = useCallback(() => {
    if (!isReplyOpen && !isEditMode) return;
    const currentReplyValue = replyFieldRef.current?.value?.trim() ?? "";

    if (currentReplyValue.length === 0 && !isEditMode) {
      setIsReplyOpen(false);
      return;
    }

    if (isShake) return;
    setIsShake(true);
    runTimeout(() => setIsShake(false), 5000);
  }, [isReplyOpen, isShake, isEditMode, runTimeout]);

  function handleMarkedTextClick(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    const el = document.querySelector(`[data-persistent-comment-id="${comment.id}"]`) as HTMLSpanElement | null;
    if (!el) return;
    scrollIntoView(el, () => {
      el.style.backgroundColor = "var(--innovera-yellow300)";
      void el.offsetWidth;

      runTimeout(() => {
        el.style.backgroundColor = "var(--innovera-yellow100)";
      }, 1000);
    });
  }

  return (
    <ClickAwayListener
      onClickAway={handleClickAway}
      triggerOn="click"
      insideSelectors={["[data-radix-popper-content-wrapper]"]}
    >
      <div
        className={cn(
          "bg-white rounded-[8px] p-3 group border border-innoveraZinc-100 transition-colors ease duration-300",
          !isReplyOpen && "hover:bg-innoveraZinc-50",
          isShake && "shake-animation"
        )}
        onClick={handleReplyToComment}
        data-testid={`comment-${comment.id}`}
        id={comment.id}
      >
        <div
          className={cn(
            "flex justify-start gap-2 cursor-pointer",
            (comment.replies.length > 0 || isReplyOpen) && "mb-3"
          )}
        >
          <div className="flex flex-col items-center gap-1 overflow-hidden">
            <Avatar className="size-5">
              <AvatarFallback className="text-[10px]">{getUserInitials(comment.author)}</AvatarFallback>
            </Avatar>
            <div
              className={cn(
                "border-l-2 border-innoveraZinc-150 h-full mx-auto opacity-0 transition-opacity",
                isReplyOpen && !isEditMode && "opacity-100"
              )}
            />
          </div>
          <div className="w-full flex gap-2 flex-col -mt-[9px]">
            <div className={cn("flex gap-1 items-center mt-1")}>
              <FigmaTypography variant="label-2" className="font-medium truncate" style={{ lineHeight: "120%" }}>
                {getUserFullName(comment.author)}
              </FigmaTypography>
              <FigmaTypography variant="label-3" className="text-innoveraZinc-500 font-light">
                {formatRelativeTime(comment.createdAt)}
              </FigmaTypography>
              <div className="flex items-center gap-1 ml-auto">
                <CommentActions
                  commentId={comment.id}
                  onDelete={onDelete}
                  isCanEdit={isCanEdit}
                  withResolveButton
                  isVisible={isReplyOpen}
                  onEdit={() => setIsEditMode(true)}
                />
              </div>
            </div>

            {markedText && (
              <FigmaTypography
                onClick={handleMarkedTextClick}
                variant="body-2"
                className={cn(
                  "ml-0.5 pl-4 text-innoveraYellow-700 bg-innoveraYellow-50 hover:bg-innoveraYellow-100",
                  "border-l-2 border-innoveraYellow-300",
                  "transition-colors ease duration-300"
                )}
              >
                {markedText}
              </FigmaTypography>
            )}
            {isEditMode && userId && (
              <ReplyEditInputField
                ref={editFieldRef}
                userId={userId}
                commentId={comment.id}
                commentContent={comment.content}
                isEditField
                onSubmit={() => setIsEditMode(false)}
                onCancel={() => setIsEditMode(false)}
                className="-ml-[26px]"
              />
            )}
            {!isEditMode && (
              <FigmaTypography variant="body-2" className="ml-1 text-innoveraZinc-900">
                {comment.content}
              </FigmaTypography>
            )}
          </div>
        </div>

        {comment.replies.length > 0 && userId && (
          <CommentReplies
            commentReplies={comment.replies}
            parentId={comment.id}
            isReplyOpen={isReplyOpen}
            userId={userId}
            onDelete={onDelete}
          />
        )}
        {isReplyOpen && userId && (
          <ReplyEditInputField
            ref={replyFieldRef}
            userId={userId}
            firstName={firstName}
            lastName={lastName}
            commentId={comment.id}
            onCancel={() => setIsReplyOpen(false)}
          />
        )}
      </div>
    </ClickAwayListener>
  );
});
